/**
 * Admin Wave AW7 — カラムマッピング (任意 CSV ヘッダ → 投稿フィールド) 移植 (docs/108 Critical #9 / High1-2)。
 *
 * old2 `apps/admin/src/lib/import/columnMapping.ts` の TARGET_FIELDS / autoDetectMapping /
 * applyMapping を v2 に移植。**ただし v2 は importExecutor callable が CSV テキストを server-side で
 * parse する設計** (functions/src/admin/importExecutor.ts、KNOWN_COLUMNS で内部名を期待) のため、
 * old2 のように `MappedPost[]` を組んで専用 callable に渡すのではなく、
 *
 *   1. CSV ヘッダを v2 内部名 (importExecutor の KNOWN_COLUMNS と完全一致) に **rename** し
 *   2. locationCombined / createdAtDate+Time を split / merge して列を合成し
 *   3. **CSV 文字列を再構築** して既存の importExecutor callable にそのまま流す
 *
 * という client-side apply 方式を採る。これにより **functions / backend 変更ゼロ** で
 * カラムマッピングを実現する (docs/108 の「NO backend change」要件)。
 *
 * importExecutor が認識する内部名 (KNOWN_COLUMNS、functions/src/admin/importExecutor.ts L198-218):
 *   title / comment / lat / lng / createdAt / authorUid / authorNickname / visibility / source /
 *   sessionId / photoPath / photoThumbPath / photoUrl / photoThumbUrl / agreeCount / agreeUids /
 *   category / subType
 * これら以外の列 (originalId / HappinessScore / userAge 等) は importExecutor が
 * `extraFields` に自動集約する (KNOWN_COLUMNS 外は extraFields)。よって ECW 固有列・ユーザー属性・
 * originalId・写真ファイル名は **その出力ヘッダ名のまま流す** だけで extraFields に入る。
 */

import type { DistrictMode } from '@citizen-walk/shared-types';
import type { ImportMode } from './csvParser';

/** マッピング対象のフィールド key (UI の論理 key、内部 CSV 列名とは別)。 */
export type MappingFieldKey =
  | 'title'
  | 'latitude'
  | 'longitude'
  | 'locationCombined'
  | 'comment'
  | 'authorNickname'
  | 'postType'
  | 'subType'
  | 'createdAtDate'
  | 'createdAtTime'
  | 'photoFilename'
  | 'originalId'
  | 'userAge'
  | 'userGender'
  | 'userAddress'
  | 'userAffiliation'
  | 'userGroup'
  | 'likeIt'
  | 'likeItCount'
  | 'happinessScore'
  | 'healthScore'
  | 'scope'
  | 'duration';

/** 論理 key → 選択された CSV ヘッダ名 (null = 未マッピング)。 */
export type ColumnMapping = Record<MappingFieldKey, string | null>;

export interface TargetFieldDef {
  key: MappingFieldKey;
  label: string;
  required: boolean;
  /** 表示する mode (未指定なら全 mode)。 */
  modes?: DistrictMode[];
  /** グループ分け (user 属性 / agree いいね 等)。 */
  group?: 'post' | 'user' | 'agree';
}

/**
 * マッピング対象フィールド定義 (old2 TARGET_FIELDS 踏襲)。
 * required は v2 importExecutor の必須列 (title|comment いずれか / lat / lng / createdAt) と整合させ、
 * title を必須にしつつ位置・日時は applyMapping で合成可能なため UI 上は任意扱い (preview で警告)。
 */
export const TARGET_FIELDS: TargetFieldDef[] = [
  { key: 'title', label: 'タイトル', required: true, group: 'post' },
  { key: 'comment', label: 'コメント', required: false, group: 'post' },
  { key: 'latitude', label: '緯度 (lat)', required: false, group: 'post' },
  { key: 'longitude', label: '経度 (lng)', required: false, group: 'post' },
  {
    key: 'locationCombined',
    label: '位置（結合列 "lat, lng"）',
    required: false,
    group: 'post',
  },
  { key: 'createdAtDate', label: '日付', required: false, group: 'post' },
  { key: 'createdAtTime', label: '時刻', required: false, group: 'post' },
  { key: 'authorNickname', label: '投稿者ID (user_name)', required: false, group: 'post' },
  {
    key: 'postType',
    label: '投稿タイプ',
    required: false,
    modes: ['bousai', 'kids-bousai'],
    group: 'post',
  },
  {
    key: 'subType',
    label: 'サブタイプ',
    required: false,
    modes: ['bousai', 'kids-bousai'],
    group: 'post',
  },
  { key: 'photoFilename', label: '写真ファイル名', required: false, group: 'post' },
  { key: 'originalId', label: '元ID (originalId)', required: false, group: 'post' },
  // ユーザー属性 (v2 では post の extraFields に保存)
  { key: 'userAge', label: 'ユーザー年代', required: false, group: 'user' },
  { key: 'userGender', label: 'ユーザー性別', required: false, group: 'user' },
  { key: 'userAddress', label: 'ユーザー地区関係', required: false, group: 'user' },
  { key: 'userAffiliation', label: 'ユーザー所属', required: false, group: 'user' },
  { key: 'userGroup', label: '参加グループ/セッション', required: false, group: 'user' },
  // いいね
  { key: 'likeIt', label: 'いいねユーザーID一覧', required: false, group: 'agree' },
  { key: 'likeItCount', label: 'いいね数', required: false, group: 'agree' },
  // ECW / walker 固有
  {
    key: 'happinessScore',
    label: 'HappinessScore',
    required: false,
    modes: ['ecw', 'walker'],
    group: 'post',
  },
  {
    key: 'healthScore',
    label: 'HealthScore',
    required: false,
    modes: ['ecw', 'walker'],
    group: 'post',
  },
  {
    key: 'scope',
    label: '主体範囲 (scope)',
    required: false,
    modes: ['ecw', 'walker'],
    group: 'post',
  },
  {
    key: 'duration',
    label: '効果時間 (duration)',
    required: false,
    modes: ['ecw', 'walker'],
    group: 'post',
  },
];

/** CSV ヘッダー名からの自動マッピング規則 (old2 AUTO_MAP 踏襲、大文字小文字無視)。 */
const AUTO_MAP: Array<{ pattern: RegExp; target: MappingFieldKey }> = [
  { pattern: /^title$/i, target: 'title' },
  { pattern: /^lat$/i, target: 'latitude' },
  { pattern: /^latitude$/i, target: 'latitude' },
  { pattern: /^long$/i, target: 'longitude' },
  { pattern: /^longitude$/i, target: 'longitude' },
  { pattern: /^lng$/i, target: 'longitude' },
  { pattern: /^location$/i, target: 'locationCombined' },
  { pattern: /^latlong$/i, target: 'locationCombined' },
  { pattern: /^photo\d?$/i, target: 'photoFilename' },
  { pattern: /^user_?name$/i, target: 'authorNickname' },
  { pattern: /^author/i, target: 'authorNickname' },
  { pattern: /^detail$/i, target: 'comment' },
  { pattern: /^comment$/i, target: 'comment' },
  // 旧 CSV の category / type 列は投稿タイプ (extraFields.postType) に取り込む。
  { pattern: /^category$/i, target: 'postType' },
  { pattern: /^type$/i, target: 'postType' },
  { pattern: /^post_?type$/i, target: 'postType' },
  { pattern: /^sub_?type$/i, target: 'subType' },
  { pattern: /^date$/i, target: 'createdAtDate' },
  { pattern: /^created_?at$/i, target: 'createdAtDate' },
  { pattern: /^datetime$/i, target: 'createdAtDate' },
  { pattern: /^time$/i, target: 'createdAtTime' },
  { pattern: /^id$/i, target: 'originalId' },
  // ユーザー属性
  { pattern: /^user_?age$/i, target: 'userAge' },
  { pattern: /^user_?gender$/i, target: 'userGender' },
  { pattern: /^user_?address$/i, target: 'userAddress' },
  { pattern: /^user_?affiliation$/i, target: 'userAffiliation' },
  { pattern: /^user_?group$/i, target: 'userGroup' },
  // いいね
  { pattern: /^like_?it_?count$/i, target: 'likeItCount' },
  { pattern: /^like_?it$/i, target: 'likeIt' },
  // ECW
  { pattern: /^happinessscore$/i, target: 'happinessScore' },
  { pattern: /^happpy_ot_not$/i, target: 'happinessScore' },
  { pattern: /^healthscore$/i, target: 'healthScore' },
  { pattern: /^healthy_or_not$/i, target: 'healthScore' },
  { pattern: /^scope$/i, target: 'scope' },
  { pattern: /^duration$/i, target: 'duration' },
];

export function emptyMapping(): ColumnMapping {
  const m = {} as ColumnMapping;
  for (const f of TARGET_FIELDS) m[f.key] = null;
  return m;
}

/**
 * CSV ヘッダ群から自動マッピングを推定 (old2 autoDetectMapping 踏襲)。
 * AUTO_MAP の順で照合し、既に使われたヘッダは再利用しない (1 列 → 1 target)。
 */
export function autoDetectMapping(headers: string[]): ColumnMapping {
  const mapping = emptyMapping();
  const used = new Set<string>();
  for (const { pattern, target } of AUTO_MAP) {
    if (mapping[target]) continue;
    const match = headers.find((h) => pattern.test(h) && !used.has(h));
    if (match) {
      mapping[target] = match;
      used.add(match);
    }
  }
  return mapping;
}

/** ImportMode → DistrictMode 変換 (post-* mode から target field の modes フィルタに使う)。 */
export function importModeToDistrictMode(mode: ImportMode): DistrictMode | null {
  switch (mode) {
    case 'post-bousai':
      return 'bousai';
    case 'post-walker':
      return 'walker';
    case 'post-ecw':
      return 'ecw';
    default:
      return null;
  }
}

/** target field が現 mode で表示対象か。 */
export function isFieldVisibleForMode(f: TargetFieldDef, mode: DistrictMode | null): boolean {
  if (!f.modes) return true;
  if (!mode) return true;
  return f.modes.includes(mode);
}

/* ===== applyMapping (client-side CSV 再構築) ===== */

export type LocationSource = 'latLng' | 'combined' | 'none';

/** preview / 写真マッチ用に保持する 1 行の中間像。 */
export interface MappedRow {
  /** 写真マッチ・上書き用の安定 index (元 rows 配列の index)。 */
  rowIndex: number;
  title: string;
  latitude: number | null;
  longitude: number | null;
  locationSource: LocationSource;
  authorNickname: string;
  comment: string | null;
  photoFilename: string | null;
  originalId: string | null;
  createdAt: string | null;
  /** importExecutor へ送る再構築済の 1 行 (内部名キー)。 */
  internal: Record<string, string>;
  /** いいねユーザー名一覧 (preview 統計用)。 */
  likeItUserNames: string[];
  likeItCount: number;
  /** 参加グループ/セッション名 (preview 統計用)。 */
  userGroup: string | null;
}

export interface ApplyMappingResult {
  rows: MappedRow[];
  /** 行単位の警告 (位置なし / タイトル空 等)。 */
  warnings: { rowIndex: number; message: string }[];
}

function num(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const v = Number.parseFloat(raw);
  return Number.isFinite(v) ? v : null;
}

export interface ApplyMappingOptions {
  /** sessionId 列を全行に一律付与する場合の値 (セッション割当ドロップダウン)。 */
  sessionId?: string | null;
  /** 位置なし行を (0,0) 暫定登録するか (false なら除外、docs/108 Low)。 */
  includeNoLocation?: boolean;
}

/**
 * mapping を rows に適用し、(1) preview 用の中間像と (2) importExecutor へ送る内部名行を生成する。
 *
 * - 位置: lat/lng 個別列を優先、無ければ locationCombined を `,` で split (High1)。両方無ければ none。
 * - 日時: createdAtDate (+ createdAtTime) を ISO 文字列に合成 → 内部名 `createdAt`。
 * - 投稿者: authorNickname 列が無ければ 'インポート' を default 値とする (old2 踏襲)。
 * - いいね: likeIt (カンマ区切り名) → `agreeUids` (カンマ結合)、likeItCount → `agreeCount`。
 * - ECW 固有 / ユーザー属性 / originalId / 写真ファイル名: 内部名以外のヘッダ名のまま流し
 *   importExecutor の extraFields に集約させる (High2)。
 */
export function applyMapping(
  rows: Record<string, string>[],
  mapping: ColumnMapping,
  mode: DistrictMode | null,
  options: ApplyMappingOptions = {},
): ApplyMappingResult {
  const { sessionId = null, includeNoLocation = true } = options;
  const out: MappedRow[] = [];
  const warnings: { rowIndex: number; message: string }[] = [];
  const isEcw = mode === 'ecw' || mode === 'walker';

  rows.forEach((row, i) => {
    const title = mapping.title ? (row[mapping.title] ?? '').trim() : '';
    const comment = mapping.comment ? (row[mapping.comment] ?? '').trim() : '';
    if (!title && !comment) {
      warnings.push({ rowIndex: i, message: 'タイトル / コメントが両方空です（除外）' });
      return;
    }

    // 位置解決
    let lat: number | null = null;
    let lng: number | null = null;
    let locationSource: LocationSource = 'none';
    if (mapping.latitude && mapping.longitude) {
      const la = num(row[mapping.latitude]);
      const lo = num(row[mapping.longitude]);
      if (la !== null && lo !== null && !(la === 0 && lo === 0)) {
        lat = la;
        lng = lo;
        locationSource = 'latLng';
      }
    }
    if (locationSource === 'none' && mapping.locationCombined) {
      const raw = row[mapping.locationCombined] ?? '';
      const parts = raw.split(',').map((s) => s.trim());
      const la = num(parts[0]);
      const lo = num(parts[1]);
      if (la !== null && lo !== null && !(la === 0 && lo === 0)) {
        lat = la;
        lng = lo;
        locationSource = 'combined';
      }
    }
    if (locationSource === 'none') {
      if (!includeNoLocation) {
        warnings.push({ rowIndex: i, message: '位置情報なし（除外）' });
        return;
      }
      lat = 0;
      lng = 0;
      warnings.push({ rowIndex: i, message: '位置情報なし — 暫定座標 (0, 0) で登録されます' });
    }

    // 日時合成
    let createdAt: string | null = null;
    if (mapping.createdAtDate) {
      const d = (row[mapping.createdAtDate] ?? '').trim();
      const t = mapping.createdAtTime ? (row[mapping.createdAtTime] ?? '').trim() : '';
      if (d) {
        // date 列が既に full datetime (ISO や日時を含む) の場合はそのまま解釈し、time 列を無視。
        const isFullDateTime = /[T ]\d{1,2}:\d{2}/.test(d) || /Z|[+-]\d{2}:?\d{2}$/.test(d);
        if (isFullDateTime) {
          const parsed = new Date(d);
          createdAt = Number.isNaN(parsed.getTime()) ? d : parsed.toISOString();
        } else {
          const normalized = d.replace(/\//g, '-');
          const parsed = new Date(`${normalized}T${t || '00:00:00'}`);
          createdAt = Number.isNaN(parsed.getTime()) ? d : parsed.toISOString();
        }
      }
    }

    const authorNickname = mapping.authorNickname
      ? (row[mapping.authorNickname] ?? '').trim() || 'インポート'
      : 'インポート';
    const photoFilename = mapping.photoFilename
      ? (row[mapping.photoFilename] ?? '').trim() || null
      : null;
    const originalId = mapping.originalId ? (row[mapping.originalId] ?? '').trim() || null : null;

    // mapping された列の値を trim して取得 (未マッピング / 空は '')。
    const cell = (key: MappingFieldKey): string => {
      const col = mapping[key];
      if (!col) return '';
      return (row[col] ?? '').trim();
    };

    // いいね
    let likeItUserNames: string[] = [];
    const likeItRaw = cell('likeIt');
    if (likeItRaw) {
      likeItUserNames = likeItRaw
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    const likeItCount = mapping.likeItCount
      ? Number.parseInt(cell('likeItCount'), 10) || 0
      : likeItUserNames.length;

    const userGroup = cell('userGroup') || null;

    // importExecutor へ送る内部名行 (KNOWN_COLUMNS と完全一致 + extraFields 行き)
    const resolvedTitle = title || comment.slice(0, 24);
    const internal: Record<string, string> = {};
    internal.title = resolvedTitle;
    if (comment) internal.comment = comment;
    // 位置 (none でも 0,0 で書く。importExecutor は lat/lng 必須)
    internal.lat = String(lat ?? 0);
    internal.lng = String(lng ?? 0);
    // createdAt は必須列。合成できなければ現在時刻を fallback (importExecutor が空を弾くため)
    internal.createdAt = createdAt ?? new Date().toISOString();
    if (authorNickname) internal.authorNickname = authorNickname;
    const postTypeVal = cell('postType');
    if (postTypeVal) internal.postType = postTypeVal;
    const subTypeVal = cell('subType');
    if (subTypeVal) internal.subType = subTypeVal;
    if (sessionId) internal.sessionId = sessionId;
    // いいね → agreeUids / agreeCount (importExecutor が agrees subcollection へ)
    if (likeItUserNames.length > 0) internal.agreeUids = likeItUserNames.join(',');
    if (mapping.likeItCount) internal.agreeCount = String(likeItCount);
    // 写真ファイル名は extraFields に残す (Drive relink 用)。photoPath は後段で上書き。
    if (photoFilename) internal.photoFilename = photoFilename;
    // originalId は extraFields (KNOWN_COLUMNS 外なので自動集約)
    if (originalId) internal.originalId = originalId;
    // ECW 固有 → extraFields (HappinessScore / HealthScore / scope / duration)
    if (isEcw) {
      const happy = cell('happinessScore');
      if (happy) internal.HappinessScore = happy;
      const health = cell('healthScore');
      if (health) internal.HealthScore = health;
      const scopeVal = cell('scope');
      if (scopeVal) internal.scope = scopeVal;
      const durationVal = cell('duration');
      if (durationVal) internal.duration = durationVal;
    }
    // ユーザー属性 → extraFields (post に保存。v2 importExecutor は participant を作らない)
    const userAge = cell('userAge');
    if (userAge) internal.userAge = userAge;
    const userGender = cell('userGender');
    if (userGender) internal.userGender = userGender;
    const userAddress = cell('userAddress');
    if (userAddress) internal.userAddress = userAddress;
    const userAffiliation = cell('userAffiliation');
    if (userAffiliation) internal.userAffiliation = userAffiliation;
    if (userGroup) internal.userGroup = userGroup;

    out.push({
      rowIndex: i,
      title: resolvedTitle,
      latitude: lat,
      longitude: lng,
      locationSource,
      authorNickname,
      comment: comment || null,
      photoFilename,
      originalId,
      createdAt,
      internal,
      likeItUserNames,
      likeItCount,
      userGroup,
    });
  });

  return { rows: out, warnings };
}

/** RFC4180 1 フィールドの quote (カンマ / 改行 / 引用符を含むとき `"..."` で囲い `"` を `""` に)。 */
export function csvQuoteField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * MappedRow[] から importExecutor が parse できる CSV 文字列を再構築する。
 * すべての行に現れる内部名キーの union を header に並べる (列の有無は行ごとに異なるため)。
 */
export function buildInternalCsv(mappedRows: MappedRow[]): string {
  const headerSet = new Set<string>();
  for (const r of mappedRows) {
    for (const k of Object.keys(r.internal)) headerSet.add(k);
  }
  // 必須列 (lat/lng/createdAt) を確実に含める
  for (const required of ['title', 'lat', 'lng', 'createdAt']) headerSet.add(required);
  const headers = [...headerSet];
  const lines: string[] = [headers.map(csvQuoteField).join(',')];
  for (const r of mappedRows) {
    const cols = headers.map((h) => csvQuoteField(r.internal[h] ?? ''));
    lines.push(cols.join(','));
  }
  return lines.join('\r\n');
}

/** preview 統計: 位置解決ソースごとの件数。 */
export function summarizeLocation(rows: MappedRow[]): {
  latLng: number;
  combined: number;
  none: number;
} {
  let latLng = 0;
  let combined = 0;
  let none = 0;
  for (const r of rows) {
    if (r.locationSource === 'latLng') latLng++;
    else if (r.locationSource === 'combined') combined++;
    else none++;
  }
  return { latLng, combined, none };
}

/** preview 統計: ユニークセッション名一覧 (userGroup 列から)。 */
export function extractSessionNames(rows: MappedRow[]): string[] {
  const names = new Set<string>();
  for (const r of rows) {
    if (r.userGroup) names.add(r.userGroup);
  }
  return [...names];
}

/** preview 統計: ユニーク投稿者数 ('インポート' default は除外)。 */
export function extractUserCount(rows: MappedRow[]): number {
  const set = new Set<string>();
  for (const r of rows) {
    if (r.authorNickname && r.authorNickname !== 'インポート') set.add(r.authorNickname);
    for (const n of r.likeItUserNames) set.add(n);
  }
  return set.size;
}
